import { ChevronRight } from 'lucide-react'
import type { ReactNode } from 'react'

import { SheetSectionCard } from '@/components/SheetSectionCard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatRelativeTime } from '@/lib/format'

import { selectionKey, variantDisplayId, type DutyRow } from './utils'
import type { SnippetVariant } from './types'

interface SnippetVariantsTabProps {
  variants: SnippetVariant[] | null
  isLoading: boolean
  error: string | null
  onRetry: () => void
  onOpen: (row: DutyRow) => void
  /** A variant sheet's own duty, badged in the list. */
  currentDutyId?: string
}

/** The duties that have customised this snippet. Picking one drills into its diff. */
export function SnippetVariantsTab(props: SnippetVariantsTabProps) {
  return (
    <SheetSectionCard
      title={
        props.variants
          ? `Duty variants · ${props.variants.length}`
          : 'Duty variants'
      }
      body={<VariantsBody {...props} />}
    />
  )
}

function VariantsBody({
  variants,
  isLoading,
  error,
  onRetry,
  onOpen,
  currentDutyId,
}: SnippetVariantsTabProps) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-2 text-sm">
        <p className="text-destructive">{error}</p>
        <Button type="button" size="sm" variant="outline" onClick={onRetry}>
          Try again
        </Button>
      </div>
    )
  }

  if (!variants?.length) {
    return (
      <p className="text-sm text-muted-foreground">
        No duties have a variant of this snippet yet.
      </p>
    )
  }

  return (
    <Table containerClassName="overflow-hidden rounded-lg border border-neutral-subtle-border">
      <TableHeader>
        <TableRow>
          <TableHead className="w-20">ID</TableHead>
          <TableHead>Duty</TableHead>
          <TableHead className="w-32">Updated</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {variants.map((variant, index) => {
          const row: DutyRow = { kind: 'duty', dutyId: variant.duty_id }
          return (
            <TableRow key={variant.duty_id} className="relative">
              <TableCell className="font-mono text-xs text-muted-foreground">
                {variantDisplayId(index)}
              </TableCell>
              <TableCell>
                <DiffRowButton
                  rowKey={selectionKey(row)}
                  onOpen={() => onOpen(row)}
                  badges={
                    variant.duty_id === currentDutyId && (
                      <Badge>This duty</Badge>
                    )
                  }>
                  {variant.duty_name}
                </DiffRowButton>
              </TableCell>
              <TableCell className="whitespace-nowrap text-muted-foreground">
                {formatRelativeTime(variant.updated_at)}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

/**
 * Stretched button: the overlay makes the whole row open the diff, and it is
 * keyboard-reachable. It covers every cell, so any other control in the row
 * needs `relative z-10`, as `badges` get. `rowKey` is where Back returns focus.
 */
export function DiffRowButton({
  rowKey,
  onOpen,
  badges,
  children,
}: {
  rowKey: string
  onOpen: () => void
  badges?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        data-row-key={rowKey}
        onClick={onOpen}
        className="min-w-0 cursor-pointer truncate text-left after:absolute after:inset-0 focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-ring focus-visible:after:ring-inset">
        {children}
      </button>
      {badges && (
        <span className="relative z-10 flex shrink-0 items-center gap-1">
          {badges}
        </span>
      )}
      <ChevronRight
        aria-hidden="true"
        className="ml-auto size-4 shrink-0 text-neutral-low-contrast"
      />
    </div>
  )
}
